import { useEffect, useRef, useCallback } from "react";
import {
  FolderTree, Bug, FileCode, Lightbulb,
} from "lucide-react";
import { useChatTurns } from "@/hooks/useChatTurns";
import { TextBlock } from "./blocks/TextBlock";
import { ToolUseBlock } from "./blocks/ToolUseBlock";
import { ThinkingBlock } from "./blocks/ThinkingBlock";
import { DiffBlock } from "./blocks/DiffBlock";
import { ImageBlock } from "./blocks/ImageBlock";
import { PermissionBlock } from "./PermissionBlock";
import { LiveActivityBar } from "./live/LiveActivityBar";
import { PROVIDER_META, type AiChatSession, type AiProvider } from "@/types/ai-chat";
import type { ContentBlock, ChatTurn } from "@/types/content-blocks";

const QUICK_ACTIONS = [
  { icon: <FolderTree size={14} strokeWidth={1.5} />, label: "Explain architecture", prompt: "Can you explain the project architecture?" },
  { icon: <Bug size={14} strokeWidth={1.5} />, label: "Debug issue", prompt: "Help me debug the latest error in the logs" },
  { icon: <FileCode size={14} strokeWidth={1.5} />, label: "Write tests", prompt: "Generate tests for the main components" },
  { icon: <Lightbulb size={14} strokeWidth={1.5} />, label: "Suggest improvements", prompt: "What improvements would you suggest for this project?" },
];

function WelcomeScreen({ provider, onAction }: { provider: AiProvider; onAction: (prompt: string) => void }) {
  const meta = PROVIDER_META[provider];
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <div className="flex flex-col items-center gap-4 max-w-[480px]">
        <img src={meta.logo} alt="" className="w-10 h-10 rounded-lg object-contain opacity-80" />
        <div className="text-center">
          <p className="text-[14px] text-colony-text-primary font-medium">Chat with {meta.label}</p>
          <p className="text-[12px] text-colony-text-tertiary mt-1">Ask anything about your project</p>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2 w-full max-w-[400px]">
          {QUICK_ACTIONS.map((action, i) => (
            <button
              key={i}
              onClick={() => onAction(action.prompt)}
              className="flex items-center gap-2.5 h-10 px-3.5 rounded-lg border border-colony-border-subtle bg-white/[0.02] hover:bg-colony-surface-hover text-[12px] text-colony-text-secondary hover:text-colony-text-primary transition-all cursor-pointer text-left"
            >
              <span className="text-colony-text-tertiary shrink-0">{action.icon}</span>
              <span className="truncate">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlockRenderer({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case "text":
      return <TextBlock text={block.text} />;
    case "tool_use":
      return <ToolUseBlock block={block} />;
    case "thinking":
      return <ThinkingBlock text={block.text} isStreaming={block.isStreaming} />;
    case "diff":
      return <DiffBlock filePath={block.filePath} hunks={block.hunks} />;
    case "image":
      return <ImageBlock url={block.url} alt={block.alt} />;
    case "permission":
      return (
        <PermissionBlock
          block={block}
          onApprove={() => {}}
          onDeny={() => {}}
          onAlwaysAllow={() => {}}
        />
      );
    default:
      return null;
  }
}

function UserTurn({ turn }: { turn: ChatTurn }) {
  const textBlock = turn.blocks.find((b) => b.type === "text");
  const text = textBlock?.type === "text" ? textBlock.text : "";

  return (
    <div className="py-4">
      <div className="max-w-[720px] mx-auto px-5">
        <div className="text-[13px] text-colony-text-heading whitespace-pre-wrap leading-[1.6]">{text}</div>
      </div>
    </div>
  );
}

function AssistantTurn({ turn, provider }: { turn: ChatTurn; provider: AiProvider }) {
  const meta = PROVIDER_META[provider];

  return (
    <div className="py-4">
      <div className="max-w-[720px] mx-auto px-5">
        <div className="flex items-center gap-2 mb-2">
          <img src={meta.logo} alt="" className="w-4 h-4 rounded-sm object-contain opacity-70" />
          <span className="text-[11px] font-medium text-colony-text-tertiary tracking-wide uppercase">{meta.label}</span>
        </div>
        <div className="space-y-0.5">
          {turn.blocks.map((block, i) => (
            <BlockRenderer key={i} block={block} />
          ))}
        </div>
        {turn.isStreaming && (
          <span className="inline-block w-1.5 h-4 bg-colony-text-secondary/60 animate-pulse align-text-bottom" />
        )}
        {turn.costUsd !== undefined && (
          <div className="flex items-center gap-2 mt-2 text-[10px] text-colony-text-tertiary">
            <span>${turn.costUsd.toFixed(4)}</span>
            {turn.tokenCount && (
              <span>{(turn.tokenCount.input + turn.tokenCount.output).toLocaleString()} tokens</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SystemTurn({ turn }: { turn: ChatTurn }) {
  const textBlock = turn.blocks.find((b) => b.type === "text");
  const text = textBlock?.type === "text" ? textBlock.text : "";

  return (
    <div className="py-2">
      <div className="max-w-[720px] mx-auto px-5">
        <div className="text-[11px] text-colony-text-tertiary bg-white/[0.02] rounded-md px-3 py-1.5 whitespace-pre-wrap">
          {text}
        </div>
      </div>
    </div>
  );
}

interface Props {
  session: AiChatSession | null;
  provider: AiProvider;
  isSessionRunning: boolean;
  onQuickAction: (prompt: string) => void;
}

export function ChatConversation({ session, provider, isSessionRunning, onQuickAction }: Props) {
  const turns = useChatTurns(session);
  const scrollRef = useRef<HTMLDivElement>(null);
  const shouldAutoScroll = useRef(true);

  const hasContent = turns.length > 0;
  const showLiveActivity = isSessionRunning && hasContent && session !== null;

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    shouldAutoScroll.current = scrollHeight - scrollTop - clientHeight < 80;
  }, []);

  useEffect(() => {
    if (scrollRef.current && shouldAutoScroll.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, showLiveActivity]);

  if (!hasContent) {
    return <WelcomeScreen provider={provider} onAction={onQuickAction} />;
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 min-h-0 overflow-y-auto"
      style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}
    >
      {turns.map((turn) => {
        switch (turn.role) {
          case "user":
            return <UserTurn key={turn.id} turn={turn} />;
          case "assistant":
            return <AssistantTurn key={turn.id} turn={turn} provider={turn.provider ?? provider} />;
          case "system":
            return <SystemTurn key={turn.id} turn={turn} />;
        }
      })}
      {showLiveActivity && session && (
        <div className="py-4">
          <div className="max-w-[720px] mx-auto px-5">
            <LiveActivityBar session={session} />
          </div>
        </div>
      )}
      <div className="h-4" />
    </div>
  );
}
